import { Alert, Button, Card, DatePicker, Space, Table, Tag, Typography } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import api from '@/services/api.service';
import { getUnifiedTasks, type UnifiedTaskRow } from '@/services/unifiedTask.service';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';
import { useLmsAccess } from '@/pages/education/utils/lmsAccess';

type WorkspaceDay = {
  as_of: string;
  day_of_week: number;
  empty_reason?: string | null;
  staff_link?: {
    id: string;
    employee_code?: string;
    first_name?: string;
    last_name?: string;
  } | null;
  session?: { id: string; code?: string; name?: string; status?: string } | null;
  periods: Array<{
    slot_id: string;
    period_code?: string;
    period_name?: string;
    start_time?: string;
    end_time?: string;
    subject_code?: string;
    subject_name?: string;
    section_id?: string;
    section_name?: string;
    grade_code?: string;
    room_code?: string;
    take_attendance_path?: string;
  }>;
  covers: Array<{
    cover_id: string;
    substitution_day_id?: string;
    period_code?: string;
    subject_code?: string;
    section_name?: string;
    grade_code?: string;
    room_code?: string;
    absent_first_name?: string;
    absent_last_name?: string;
  }>;
  attendance: Array<{
    section_id: string;
    section_name?: string;
    grade_code?: string;
    register_id?: string | null;
    register_status?: string | null;
    needs_marking?: boolean;
    action_path?: string;
  }>;
  links?: {
    timetable_teacher?: string;
    substitutions?: string;
    attendance?: string;
    tasks?: string;
  };
};

export default function TeacherWorkspacePage() {
  const { t } = useTranslation('education');
  const navigate = useNavigate();
  const [asOf, setAsOf] = useState<Dayjs>(() => dayjs());
  const { canView: canLms } = useLmsAccess();

  const day = useQuery({
    queryKey: ['education-workspace-my-day', asOf.format('YYYY-MM-DD')],
    queryFn: async () => {
      const res = await api.get<WorkspaceDay>('/education/workspace/my-day', {
        params: { date: asOf.format('YYYY-MM-DD') },
      });
      return res.data;
    },
  });

  const tasks = useQuery({
    queryKey: ['education-workspace-tasks'],
    queryFn: async () => {
      try {
        return await getUnifiedTasks({
          assigned_to: 'me',
          open_only: true,
          limit: 10,
          offset: 0,
        });
      } catch {
        return [] as UnifiedTaskRow[];
      }
    },
  });

  const data = day.data;
  const staffLabel = data?.staff_link
    ? `${data.staff_link.employee_code || ''} — ${[data.staff_link.first_name, data.staff_link.last_name]
        .filter(Boolean)
        .join(' ')}`.trim()
    : '';

  return (
    <EducationPageChrome
      title={t('teacherWorkspace')}
      description={t('teacherWorkspaceIntro')}
      extra={
        <Space wrap>
          <DatePicker value={asOf} onChange={(v) => v && setAsOf(v)} allowClear={false} />
          <Button onClick={() => navigate(data?.links?.tasks || '/home/tasks')}>{t('openMyTasks')}</Button>
          {canLms ? (
            <Button onClick={() => navigate('/education/lms')}>{t('lmsOpenClassroom', { defaultValue: 'Classroom (lesson plans & homework)' })}</Button>
          ) : null}
          <Button onClick={() => navigate('/education')}>{t('backToEducation', { defaultValue: 'Education home' })}</Button>
        </Space>
      }
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        {day.isError ? <Alert type="error" showIcon message={t('unavailable', { status: '' })} /> : null}
        {data?.empty_reason === 'no_staff_link' ? (
          <Alert type="warning" showIcon message={t('workspaceNoStaffLink')} />
        ) : null}
        {data?.empty_reason === 'no_session' ? (
          <Alert type="warning" showIcon message={t('workspaceNoSession')} />
        ) : null}
        {data?.staff_link && data?.session ? (
          <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
            {staffLabel} · {data.session.code} — {data.session.name} · {t(`weekday${data.day_of_week}` as 'weekday1')}
          </Typography.Paragraph>
        ) : null}

        <Card size="small" title={t('workspaceTodayPeriods')} loading={day.isLoading}>
          <Table
            rowKey="slot_id"
            size="small"
            pagination={false}
            dataSource={data?.periods ?? []}
            locale={{ emptyText: t('workspaceNoPeriods') }}
            columns={[
              {
                title: t('period'),
                render: (_: unknown, row) =>
                  `${row.period_code || ''} — ${row.period_name || ''}${
                    row.start_time ? ` (${row.start_time}–${row.end_time || ''})` : ''
                  }`,
              },
              {
                title: t('subject'),
                render: (_: unknown, row) => `${row.subject_code || ''} — ${row.subject_name || ''}`,
              },
              {
                title: t('section'),
                render: (_: unknown, row) =>
                  `${row.grade_code ? `${row.grade_code} ` : ''}${row.section_name || ''}`,
              },
              { title: t('room'), dataIndex: 'room_code', render: (v) => v || '—' },
              {
                title: t('takeAttendance'),
                width: 140,
                render: (_: unknown, row) =>
                  row.take_attendance_path ? (
                    <Button
                      type="link"
                      size="small"
                      onClick={() => navigate(row.take_attendance_path || '/education/attendance/take')}
                    >
                      {t('takeAttendance')}
                    </Button>
                  ) : (
                    '—'
                  ),
              },
            ]}
          />
          <Button
            style={{ marginTop: 12 }}
            onClick={() => navigate(data?.links?.timetable_teacher || '/education/timetable/teacher')}
          >
            {t('timetableTeacherView')}
          </Button>
        </Card>

        <Card size="small" title={t('workspaceCoverDuties')} loading={day.isLoading}>
          <Table
            rowKey="cover_id"
            size="small"
            pagination={false}
            dataSource={data?.covers ?? []}
            locale={{ emptyText: t('workspaceNoCovers') }}
            columns={[
              {
                title: t('period'),
                render: (_: unknown, row) => `${row.period_code || ''}`,
              },
              {
                title: t('section'),
                render: (_: unknown, row) =>
                  `${row.grade_code ? `${row.grade_code} ` : ''}${row.section_name || ''}`,
              },
              { title: t('subject'), dataIndex: 'subject_code' },
              {
                title: t('absentTeacher'),
                render: (_: unknown, row) =>
                  [row.absent_first_name, row.absent_last_name].filter(Boolean).join(' ') || '—',
              },
              {
                title: t('actions'),
                width: 100,
                render: (_: unknown, row) =>
                  row.substitution_day_id ? (
                    <Button
                      size="small"
                      onClick={() => navigate(`/education/substitutions/${row.substitution_day_id}`)}
                    >
                      {t('open')}
                    </Button>
                  ) : null,
              },
            ]}
          />
        </Card>

        <Card size="small" title={t('workspaceAttendance')} loading={day.isLoading}>
          <Table
            rowKey="section_id"
            size="small"
            pagination={false}
            dataSource={data?.attendance ?? []}
            locale={{ emptyText: t('workspaceNoAttendanceSections') }}
            columns={[
              {
                title: t('section'),
                render: (_: unknown, row) =>
                  `${row.grade_code ? `${row.grade_code} ` : ''}${row.section_name || ''}`,
              },
              {
                title: t('status'),
                render: (_: unknown, row) =>
                  row.needs_marking ? (
                    <Tag color="warning">{t('needsMarking')}</Tag>
                  ) : (
                    <Tag color="success">{row.register_status || t('done')}</Tag>
                  ),
              },
              {
                title: t('actions'),
                width: 140,
                render: (_: unknown, row) => (
                  <Button
                    size="small"
                    type={row.needs_marking ? 'primary' : 'default'}
                    onClick={() => navigate(row.action_path || '/education/attendance/take')}
                  >
                    {row.register_id ? t('openRegister') : t('openRegister')}
                  </Button>
                ),
              },
            ]}
          />
        </Card>

        <Card
          size="small"
          title={t('workspaceOpenTasks')}
          extra={
            <Button type="link" onClick={() => navigate('/home/tasks')}>
              {t('openMyTasks')}
            </Button>
          }
          loading={tasks.isLoading}
        >
          <Table
            rowKey="id"
            size="small"
            pagination={false}
            dataSource={tasks.data ?? []}
            locale={{ emptyText: t('workspaceNoTasks') }}
            columns={[
              {
                title: t('task', { defaultValue: 'Task' }),
                render: (_: unknown, row: UnifiedTaskRow) =>
                  row.recommended_action ||
                  row.source_document_number ||
                  `${row.module || ''} · ${row.task_type || ''}`.trim(),
              },
              {
                title: t('status'),
                dataIndex: 'status',
                width: 110,
                render: (v: string) => <Tag>{v}</Tag>,
              },
              {
                title: t('actions'),
                width: 100,
                render: (_: unknown, row: UnifiedTaskRow) => (
                  <Button
                    size="small"
                    onClick={() => navigate(row.action_link || `/home/tasks/${row.id}`)}
                  >
                    {t('open')}
                  </Button>
                ),
              },
            ]}
          />
        </Card>
      </Space>
    </EducationPageChrome>
  );
}
